import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClassForm from './ClassForm';
import './Admin.css';
import { useTranslation } from 'react-i18next';
import { uploadClassWithImages, updateClassWithImages } from '../../services/uploadService';

// Conflict dialog component
const ConflictDialog = ({ conflicts, onCancel, onProceed }) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="conflict-dialog-overlay">
      <div className="conflict-dialog">
        <h3>{t('admin.conflictsDetected')}</h3>
        <p>{t('admin.conflictsFound')}</p>
        
        <div className="conflicts-list">
          {conflicts.map((conflict, index) => (
            <div key={index} className="conflict-item">
              <p><strong>{t('admin.date')}:</strong> {conflict.date}</p>
              <p><strong>{t('admin.time')}:</strong> {conflict.time}</p>
              <p><strong>{t('admin.conflictsWith')}:</strong> {conflict.conflicting_class} ({conflict.conflicting_time})</p>
              <p><strong>{t('admin.instructor')}:</strong> {conflict.instructor}</p>
            </div>
          ))}
        </div>
        
        <div className="conflict-actions">
          <button className="cancel-button" onClick={onCancel}>
            {t('admin.cancelScheduling')}
          </button>
          <button className="proceed-button" onClick={onProceed}>
            {t('admin.scheduleAnyway')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Cancellation reason modal component
const CancellationReasonModal = ({ className, onCancel, onConfirm, enrolledCount }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError(t('admin.cancellationReasonRequired'));
      return;
    }
    onConfirm(reason);
  };

  return (
    <div className="cancellation-modal-overlay">
      <div className="cancellation-modal">
        <h3>{t('admin.cancelClassWithStudents')}</h3>
        
        <p className="cancellation-warning">
          {t('admin.cancelClassWarningWithStudents', { count: enrolledCount })}
        </p>
        
        <div className="form-group">
          <label htmlFor="cancellation-reason">{t('admin.cancellationReason')}:</label>
          <textarea
            id="cancellation-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin.cancellationReasonPlaceholder')}
            rows="4"
          />
          {error && <p className="error-message">{error}</p>}
        </div>
        
        <div className="modal-actions">
          <button className="cancel-button" onClick={onCancel}>
            {t('admin.back')}
          </button>
          <button className="confirm-button" onClick={handleSubmit}>
            {t('admin.confirmAndNotify')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClassManagement = () => {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [instructors, setInstructors] = useState([]);
  const [conflicts, setConflicts] = useState(null);
  const [pendingClassData, setPendingClassData] = useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [classToCancel, setClassToCancel] = useState(null);
  const [enrolledStudentsCount, setEnrolledStudentsCount] = useState(0);

  // Format date to YYYY-MM-DD
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Format time for display (e.g., "14:00" -> "2:00 PM")
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    
    try {
      // Handle ISO date strings
      if (timeStr.includes('T') || timeStr.includes('-')) {
        // Create a date object from the string
        const date = new Date(timeStr);
        
        // Format the time in the local timezone
        return date.toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'});
      }
      
      // Handle time strings (HH:MM)
      const [hours, minutes] = timeStr.split(':');
      const period = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      return `${formattedHours}:${minutes} ${period}`;
    } catch (err) {
      console.error('Error formatting time:', err);
      return timeStr; // Return original if parsing fails
    }
  };

  // Fetch classes for the selected date
  const fetchClasses = async () => {
    try {
      setLoading(true);
      const dateStr = formatDateToString(selectedDate);
      console.log(`Admin: Fetching classes for date: ${dateStr}`);
      
      const response = await axios.get(`classes/by-date?date=${dateStr}`);
      
      console.log('Admin: API response for classes:', response.data);
      
      // Verify that the returned classes are for the selected date
      if (response.data && Array.isArray(response.data.classes)) {
        // Additional check to ensure we're only displaying classes for the selected date
        const filteredClasses = response.data.classes.filter(yogaClass => {
          // If the class has a date property, use it for filtering
          if (yogaClass.date) {
            return yogaClass.date === dateStr;
          }
          // Otherwise assume it's for the correct date (as filtered by backend)
          return true;
        });
        
        console.log(`Admin: Filtered ${response.data.classes.length} to ${filteredClasses.length} classes for date ${dateStr}`);
        setClasses(filteredClasses);
      } else {
        console.error('Admin: Unexpected classes response format:', response.data);
        setClasses([]);
      }
      
      setError('');
    } catch (err) {
      console.error('Admin: Error fetching classes:', err);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch instructors
  const fetchInstructors = async () => {
    try {
      const response = await axios.get('classes/instructors');
      setInstructors(response.data || []);
    } catch (err) {
      console.error('Error fetching instructors:', err);
      setError('Failed to load instructor data. Please try again.');
    }
  };

  // Load classes when the date changes
  useEffect(() => {
    fetchClasses();
  }, [selectedDate]);

  // Load instructors when component mounts
  useEffect(() => {
    fetchInstructors();
  }, []);

  // Handle date change
  const handleDateChange = (e) => {
    setSelectedDate(new Date(e.target.value));
  };

  // Handle scheduling despite conflicts
  const handleForceSchedule = async () => {
    try {
      setLoading(true);
      
      // Add force_schedule flag to the pending data
      const forceData = {
        ...pendingClassData,
        force_schedule: true
      };
      
      // Ensure times are still in ISO format (might have been lost when storing in state)
      if (!forceData.start_time.includes('T')) {
        const formatTimeToISO = (timeStr) => {
          const [hours, minutes] = timeStr.split(':');
          const date = new Date(selectedDate);
          date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          
          // Store the time zone offset in minutes
          const tzOffset = date.getTimezoneOffset();
          
          // Adjust the UTC time to account for the timezone offset
          date.setMinutes(date.getMinutes() - tzOffset);
          
          return date.toISOString();
        };
        
        forceData.start_time = formatTimeToISO(forceData.start_time);
        forceData.end_time = formatTimeToISO(forceData.end_time);
      }
      
      console.log('Sending force schedule data:', forceData);
      await axios.post('classes', forceData);
      
      // Reset conflict state
      setConflicts(null);
      setPendingClassData(null);
      
      // Refresh class list and close form
      fetchClasses();
      setShowForm(false);
      setSelectedClass(null);
      
    } catch (err) {
      console.error('Error force scheduling class:', err);
      let errorMessage = 'Failed to schedule class. Please try again.';
      
      // Add more detailed error message if available
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage += ` Error: ${err.response.data.message}`;
      } else if (err.response && err.response.data && err.response.data.error) {
        errorMessage += ` Error: ${err.response.data.error}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cancel scheduling after conflict
  const handleCancelSchedule = () => {
    setConflicts(null);
    setPendingClassData(null);
  };

  // Handle form submission (create or update)
  const handleSubmit = async (classData) => {
    try {
      setLoading(true);
      setError('');
      
      // Ensure repeat_count is 1 if not using weekly repetition
      if (!classData.repeat_weekly) {
        classData.repeat_count = 1;
      }
      
      // Extract images from class data for upload
      const images = classData.images || [];
      delete classData.images; // Remove from regular data object
      
      // Format time consistently for both new and update operations
      // If start_time and end_time are not already ISO strings, format them
      if (!classData.start_time.includes('T')) {
        // Format the times properly with timezone preservation
        const formatTimeToISO = (timeStr, dateObj) => {
          const [hours, minutes] = timeStr.split(':');
          const date = new Date(dateObj);
          date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          
          // Store the time zone offset in minutes
          const tzOffset = date.getTimezoneOffset();
          
          // Adjust the UTC time to account for the timezone offset
          // This ensures the local time is stored correctly
          date.setMinutes(date.getMinutes() - tzOffset);
          
          return date.toISOString();
        };
        
        classData.start_time = formatTimeToISO(classData.start_time, selectedDate);
        classData.end_time = formatTimeToISO(classData.end_time, selectedDate);
      }
      
      if (selectedClass) {
        // Update existing class with images if provided
        console.log('Updating class with data:', classData);
        if (images && images.length > 0) {
          console.log('Uploading with images, replace_images flag:', classData.replace_images);
          // Pass the replace_images flag to ensure it's handled correctly
          await updateClassWithImages(selectedClass.id, classData, images);
        } else {
          // Process prepayment field for direct API calls too
          const processedData = {
            ...classData,
            prepayment: classData.workshop === 'yes' && classData.requires_prepayment ? classData.prepayment : null
          };
          console.log('Processed class data for direct API update:', processedData);
          await axios.put(`classes/${selectedClass.id}`, processedData);
        }
        
        fetchClasses();
        setShowForm(false);
        setSelectedClass(null);
      } else {
        // Create new class with images if provided
        try {
          console.log('Sending class data to API:', classData);
          
          let response;
          if (images && images.length > 0) {
            response = await uploadClassWithImages(classData, images);
          } else {
            // Process prepayment field for direct API calls too
            const processedData = {
              ...classData,
              prepayment: classData.workshop === 'yes' && classData.requires_prepayment ? classData.prepayment : null
            };
            console.log('Processed class data for direct API call:', processedData);
            response = await axios.post('classes', processedData);
          }
          
          console.log('API response:', response.data);
          fetchClasses();
          setShowForm(false);
          setSelectedClass(null);
        } catch (err) {
          if (err.response && err.response.status === 409) {
            // Handle scheduling conflicts
            const detectedConflicts = err.response.data.conflicts;
            
            // Store the conflicts and class data for potential force scheduling
            setConflicts(detectedConflicts);
            setPendingClassData(classData);
          } else {
            throw err; // Re-throw for the catch block below to handle
          }
        }
      }
    } catch (err) {
      console.error('Error submitting class:', err);
      let errorMessage = 'Failed to save class. Please try again.';
      
      // Add more detailed error message if available
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage += ` Error: ${err.response.data.message}`;
      } else if (err.response && err.response.data && err.response.data.error) {
        errorMessage += ` Error: ${err.response.data.error}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check for enrolled students in a class
  const checkEnrolledStudents = async (classId) => {
    try {
      const response = await axios.get(`admin/classes/${classId}/participants`);
      return response.data.participants ? response.data.participants.length : 0;
    } catch (err) {
      console.error('Error checking enrolled students:', err);
      return 0;
    }
  };

  // Handle class cancel - initial step
  const handleCancel = async (classId, className) => {
    try {
      // First check if there are any enrolled students
      const enrolledCount = await checkEnrolledStudents(classId);
      
      if (enrolledCount > 0) {
        // If there are enrolled students, show the cancellation reason modal
        setClassToCancel({ id: classId, name: className });
        setEnrolledStudentsCount(enrolledCount);
        setShowCancellationModal(true);
      } else {
        // If no students, just confirm and cancel
        if (window.confirm(t('admin.cancelClassWarning'))) {
          await cancelClass(classId);
        }
      }
    } catch (err) {
      console.error('Error in cancel process:', err);
      setError(t('admin.cancelClassError'));
    }
  };
  
  // Handle cancellation confirmation with reason
  const handleCancellationConfirm = async (reason) => {
    if (!classToCancel) return;
    
    try {
      setLoading(true);
      await cancelClass(classToCancel.id, reason);
      setShowCancellationModal(false);
      setClassToCancel(null);
    } catch (err) {
      console.error('Error confirming cancellation:', err);
      setError(t('admin.cancelClassError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cancellation modal close
  const handleCancellationCancel = () => {
    setShowCancellationModal(false);
    setClassToCancel(null);
  };
  
  // Actual class cancellation function
  const cancelClass = async (classId, reason = null) => {
    try {
      setLoading(true);
      
      // If reason is provided, send it with the request
      if (reason) {
        await axios.delete(`classes/${classId}`, { 
          headers: {
            'Content-Type': 'application/json'
          },
          data: JSON.stringify({ cancellation_reason: reason })
        });
      } else {
        await axios.delete(`classes/${classId}`);
      }
      
      fetchClasses();
    } catch (err) {
      console.error('Error cancelling class:', err);
      setError(t('admin.cancelClassError'));
      throw err; // Re-throw to be handled by the caller
    } finally {
      setLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedClass(null);
  };

  // Handle edit
  const handleEdit = (yogaClass) => {
    setSelectedClass(yogaClass);
    setShowForm(true);
  };

  // Handle new class
  const handleNewClass = () => {
    setSelectedClass(null);
    setShowForm(true);
  };

  return (
    <div className="class-management">
      <h2>{t('admin.classScheduleManagement')}</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      {conflicts && (
        <ConflictDialog 
          conflicts={conflicts}
          onCancel={handleCancelSchedule}
          onProceed={handleForceSchedule}
        />
      )}
      
      {showCancellationModal && classToCancel && (
        <CancellationReasonModal
          className={classToCancel.name}
          enrolledCount={enrolledStudentsCount}
          onCancel={handleCancellationCancel}
          onConfirm={handleCancellationConfirm}
        />
      )}
      
      <div className="admin-controls">
        <div className="date-selector">
          <label htmlFor="date">{t('admin.selectDate')}:</label>
          <input 
            type="date" 
            id="date" 
            value={formatDateToString(selectedDate)} 
            onChange={handleDateChange}
          />
        </div>
        
        <button className="add-button" onClick={handleNewClass}>
          {t('admin.addNewClass')}
        </button>
      </div>
      
      {showForm && (
        <ClassForm 
          key={`class-form-${i18n.language}`}
          instructors={instructors}
          initialData={selectedClass}
          selectedDate={selectedDate}
          onSubmit={handleSubmit}
          onCancel={handleFormCancel}
        />
      )}
      
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>{t('admin.loadingClasses')}</p>
        </div>
      ) : classes.length > 0 ? (
        <div className="classes-table">
          <table>
            <thead>
              <tr>
                <th>{t('admin.time')}</th>
                <th>{t('admin.class')}</th>
                <th>{t('admin.instructor')}</th>
                <th>{t('admin.location')}</th>
                <th>{t('admin.capacity')}</th>
                <th>{t('admin.available')}</th>
                <th>{t('admin.price')}</th>
                <th>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {classes.map(yogaClass => (
                <tr key={yogaClass.id}>
                  <td>{formatTime(yogaClass.start_time)} - {formatTime(yogaClass.end_time)}</td>
                  <td>{yogaClass.title}</td>
                  <td>{yogaClass.instructor}</td>
                  <td>{yogaClass.location}</td>
                  <td>{yogaClass.max_capacity}</td>
                  <td>{yogaClass.available_spots}</td>
                  <td>{yogaClass.price || 0} &euro;</td>
                  <td className="actions">
                    <button onClick={() => handleEdit(yogaClass)} className="edit-button">
                      {t('admin.edit')}
                    </button>
                    <button onClick={() => handleCancel(yogaClass.id, yogaClass.title)} className="cancel-button">
                      {t('admin.cancel')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="no-classes">{t('admin.noClassesScheduled')}</p>
      )}
    </div>
  );
};

export default ClassManagement; 